import React, { useState, useEffect } from 'react';
import { 
  PlusIcon, 
  ArrowLeftIcon, 
  TagIcon, 
  PencilIcon, 
  TrashIcon, 
  EyeIcon,
  ChartBarIcon,
  PaletteIcon,
  FolderIcon,
  FireIcon
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase/config';
import ConfirmDialog from '../components/ConfirmDialog';

const TagsPage = () => {
  const navigate = useNavigate();
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedTag, setSelectedTag] = useState(null);
  const [modalMode, setModalMode] = useState('add');
  const [deleteId, setDeleteId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('name');

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    color: '#3B82F6',
    category: '',
    usage_count: 0
  });

  // Predefined color options
  const colorOptions = [
    '#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6',
    '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1',
    '#14B8A6', '#F43F5E', '#A855F7', '#EAB308', '#22C55E'
  ];

  // Predefined categories
  const categories = [
    'General', 'Technology', 'Education', 'Business', 'Health',
    'Entertainment', 'Sports', 'Travel', 'Food', 'Fashion',
    'Science', 'Art', 'Music', 'Literature', 'Other'
  ];

  useEffect(() => {
    fetchTags();
  }, []);

  const fetchTags = async () => {
    try {
      setLoading(true);
      const tagsRef = collection(db, 'tags');
      const q = query(tagsRef, orderBy('name'));
      const snapshot = await getDocs(q);
      const tagsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setTags(tagsData);
    } catch (error) {
      console.error('Error fetching tags:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setSelectedTag(null);
    setModalMode('add');
    setFormData({
      name: '',
      color: '#3B82F6',
      category: 'General',
      usage_count: 0
    });
    setModalOpen(true);
  };

  const handleEdit = (tag) => {
    setSelectedTag(tag);
    setModalMode('edit');
    setFormData({
      name: tag.name || '',
      color: tag.color || '#3B82F6',
      category: tag.category || 'General',
      usage_count: tag.usage_count || 0
    });
    setModalOpen(true);
  };

  const handleView = (tag) => {
    setSelectedTag(tag);
    setModalMode('view');
    setFormData({
      name: tag.name || '',
      color: tag.color || '#3B82F6',
      category: tag.category || 'General',
      usage_count: tag.usage_count || 0
    });
    setModalOpen(true);
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setConfirmDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    setDeleteLoading(true);
    try {
      await deleteDoc(doc(db, 'tags', deleteId));
      await fetchTags();
      setConfirmDialogOpen(false);
      setDeleteId(null);
    } catch (error) {
      console.error('Error deleting tag:', error);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      if (modalMode === 'add') {
        await addDoc(collection(db, 'tags'), formData);
      } else if (modalMode === 'edit') {
        await updateDoc(doc(db, 'tags', selectedTag.id), formData);
      }
      await fetchTags();
      setModalOpen(false);
      setFormData({
        name: '',
        color: '#3B82F6',
        category: 'General',
        usage_count: 0
      });
    } catch (error) {
      console.error('Error saving tag:', error);
    }
  };

  const filteredTags = tags.filter(tag => {
    const matchesSearch = tag.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         tag.category?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || tag.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const sortedTags = [...filteredTags].sort((a, b) => {
    switch (sortBy) {
      case 'name':
        return (a.name || '').localeCompare(b.name || '');
      case 'category':
        return (a.category || '').localeCompare(b.category || '');
      case 'usage':
        return (b.usage_count || 0) - (a.usage_count || 0);
      default:
        return 0;
    }
  });

  const stats = {
    total: tags.length,
    categories: new Set(tags.map(t => t.category)).size,
    mostUsed: tags.reduce((max, tag) => tag.usage_count > max ? tag.usage_count : max, 0),
    averageUsage: tags.length > 0 ? Math.round(tags.reduce((sum, tag) => sum + (tag.usage_count || 0), 0) / tags.length) : 0
  };

  const getCategoryColor = (category) => {
    const colors = {
      'General': 'bg-gray-100 text-gray-800',
      'Technology': 'bg-blue-100 text-blue-800',
      'Education': 'bg-green-100 text-green-800',
      'Business': 'bg-purple-100 text-purple-800',
      'Health': 'bg-red-100 text-red-800',
      'Entertainment': 'bg-pink-100 text-pink-800',
      'Sports': 'bg-orange-100 text-orange-800',
      'Travel': 'bg-indigo-100 text-indigo-800',
      'Food': 'bg-yellow-100 text-yellow-800',
      'Fashion': 'bg-rose-100 text-rose-800',
      'Science': 'bg-cyan-100 text-cyan-800',
      'Art': 'bg-emerald-100 text-emerald-800',
      'Music': 'bg-violet-100 text-violet-800',
      'Literature': 'bg-amber-100 text-amber-800',
      'Other': 'bg-slate-100 text-slate-800'
    };
    return colors[category] || colors['Other'];
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <div className="bg-white shadow-lg rounded-xl border border-gray-200 p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/')}
                className="mr-4 sm:mr-6 p-2 sm:p-3 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all duration-200"
              >
                <ArrowLeftIcon className="h-5 w-5" />
              </button>
              <div>
                <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3 mb-2">
                  <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900">Tags Management</h1>
                  <span className="inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg">
                    <TagIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                    Content Organization
                  </span>
                </div>
                <p className="text-base sm:text-lg text-gray-600 flex items-center">
                  <TagIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                  <span className="hidden sm:inline">Organize and manage content tags with colors and categories</span>
                  <span className="sm:hidden">Manage content tags</span>
                </p>
              </div>
            </div>
            <button
              onClick={handleAdd}
              className="w-full sm:w-auto inline-flex items-center justify-center px-4 sm:px-6 py-2 sm:py-3 border border-transparent rounded-xl shadow-lg text-sm sm:text-base font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transform hover:scale-105 transition-all duration-200"
            >
              <PlusIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
              Add New Tag
            </button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 overflow-hidden shadow-md rounded-xl hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
          <div className="p-4 sm:p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-xs sm:text-sm font-medium uppercase tracking-wide mb-2">Total Tags</p>
                <p className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-2">{stats.total}</p>
                <p className="text-blue-100 text-xs sm:text-sm font-medium">Active tags</p>
              </div>
              <div className="bg-blue-400 bg-opacity-30 rounded-full p-3 sm:p-4 hover:bg-opacity-40 transition-all duration-300">
                <TagIcon className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 overflow-hidden shadow-md rounded-xl hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
          <div className="p-4 sm:p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-xs sm:text-sm font-medium uppercase tracking-wide mb-2">Categories</p>
                <p className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-2">{stats.categories}</p>
                <p className="text-green-100 text-xs sm:text-sm font-medium">Tag groups</p>
              </div>
              <div className="bg-green-400 bg-opacity-30 rounded-full p-3 sm:p-4 hover:bg-opacity-40 transition-all duration-300">
                <FolderIcon className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 overflow-hidden shadow-md rounded-xl hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
          <div className="p-4 sm:p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-xs sm:text-sm font-medium uppercase tracking-wide mb-2">Most Used</p>
                <p className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-2">{stats.mostUsed}</p>
                <p className="text-purple-100 text-xs sm:text-sm font-medium">Usage count</p>
              </div>
              <div className="bg-purple-400 bg-opacity-30 rounded-full p-3 sm:p-4 hover:bg-opacity-40 transition-all duration-300">
                <FireIcon className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 overflow-hidden shadow-md rounded-xl hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
          <div className="p-4 sm:p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-yellow-100 text-xs sm:text-sm font-medium uppercase tracking-wide mb-2">Avg Usage</p>
                <p className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-2">{stats.averageUsage}</p>
                <p className="text-yellow-100 text-xs sm:text-sm font-medium">Per tag</p>
              </div>
              <div className="bg-yellow-400 bg-opacity-30 rounded-full p-3 sm:p-4 hover:bg-opacity-40 transition-all duration-300">
                <ChartBarIcon className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white shadow-md rounded-xl border border-gray-200 p-4 sm:p-6 mb-6 sm:mb-8">
        <div className="flex flex-col space-y-4">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div className="flex flex-col sm:flex-row gap-4 flex-1 w-full">
              {/* Search */}
              <div className="relative flex-1 w-full sm:max-w-md">
                <input
                  type="text"
                  placeholder="Search tags..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <TagIcon className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
              </div>

              {/* Category Filter */}
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Categories</option>
                {categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>

              {/* Sort By */}
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="name">Sort by Name</option>
                <option value="category">Sort by Category</option>
                <option value="usage">Sort by Usage</option>
              </select>
            </div>
          </div>

          <div className="text-sm text-gray-500 text-center sm:text-left">
            {filteredTags.length} of {tags.length} tags
          </div>
        </div>
      </div>

      {/* Tags Grid */}
      <div className="bg-white shadow-md rounded-xl border border-gray-200 p-4 sm:p-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : sortedTags.length === 0 ? (
          <div className="text-center py-12">
            <TagIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No tags found</h3>
            <p className="text-gray-500 mb-4">
              {searchTerm || selectedCategory !== 'all' 
                ? 'Try adjusting your search or filter criteria.' 
                : 'Get started by creating your first tag.'}
            </p>
            {!searchTerm && selectedCategory === 'all' && (
              <button
                onClick={handleAdd}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Add First Tag
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
            {sortedTags.map((tag) => (
              <div
                key={tag.id}
                className="bg-white border border-gray-200 rounded-xl p-4 sm:p-6 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1"
              >
                {/* Tag Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3 flex-1 min-w-0">
                    <div
                      className="w-4 h-4 rounded-full border-2 border-white shadow-sm flex-shrink-0"
                      style={{ backgroundColor: tag.color || '#3B82F6' }}
                    />
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">
                      {tag.name}
                    </h3>
                  </div>
                  <div className="flex space-x-1 flex-shrink-0">
                    <button
                      onClick={() => handleView(tag)}
                      className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="View"
                    >
                      <EyeIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleEdit(tag)}
                      className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                      title="Edit"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(tag.id)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {/* Tag Details */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs sm:text-sm font-medium text-gray-500">Category</span>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getCategoryColor(tag.category)}`}>
                      {tag.category || 'Uncategorized'}
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-xs sm:text-sm font-medium text-gray-500">Usage Count</span>
                    <span className="text-xs sm:text-sm font-semibold text-gray-900">
                      {tag.usage_count || 0}
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-xs sm:text-sm font-medium text-gray-500">Color</span>
                    <div className="flex items-center space-x-2">
                      <div
                        className="w-5 h-5 sm:w-6 sm:h-6 rounded border-2 border-gray-200 flex-shrink-0"
                        style={{ backgroundColor: tag.color || '#3B82F6' }}
                      />
                      <span className="text-xs text-gray-500 font-mono hidden sm:block">
                        {tag.color || '#3B82F6'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Tag Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={() => setModalOpen(false)}></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full w-full max-w-sm mx-auto">
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-4 sm:px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    {modalMode === 'view' ? (
                      <EyeIcon className="h-5 w-5 sm:h-6 sm:w-6 text-white mr-2 sm:mr-3" />
                    ) : modalMode === 'edit' ? (
                      <PencilIcon className="h-5 w-5 sm:h-6 sm:w-6 text-white mr-2 sm:mr-3" />
                    ) : (
                      <PlusIcon className="h-5 w-5 sm:h-6 sm:w-6 text-white mr-2 sm:mr-3" />
                    )}
                    <h3 className="text-lg sm:text-xl font-semibold text-white">
                      {modalMode === 'add' ? 'Add New Tag' : modalMode === 'edit' ? 'Edit Tag' : 'View Tag'}
                    </h3>
                  </div>
                  <button
                    onClick={() => setModalOpen(false)}
                    className="text-white hover:text-gray-200 transition-colors"
                  >
                    <ArrowLeftIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                  </button>
                </div>
              </div>
              
              <div className="bg-white px-4 sm:px-6 py-4 sm:py-6">
                <div className="space-y-4">
                  {/* Tag Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Tag Name</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      disabled={modalMode === 'view'}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                      placeholder="Enter tag name"
                    />
                  </div>

                  {/* Category */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({...formData, category: e.target.value})}
                      disabled={modalMode === 'view'}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                    >
                      {categories.map(category => (
                        <option key={category} value={category}>{category}</option>
                      ))}
                    </select>
                  </div>

                  {/* Color Picker */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Color</label>
                    <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-3">
                      <input
                        type="color"
                        value={formData.color}
                        onChange={(e) => setFormData({...formData, color: e.target.value})}
                        disabled={modalMode === 'view'}
                        className="w-12 h-12 border border-gray-300 rounded-lg cursor-pointer disabled:cursor-not-allowed self-start"
                      />
                      <div className="flex flex-wrap gap-2">
                        {colorOptions.map((color) => (
                          <button
                            key={color}
                            type="button"
                            onClick={() => setFormData({...formData, color})}
                            disabled={modalMode === 'view'}
                            className="w-8 h-8 rounded-lg border-2 border-gray-200 hover:border-gray-400 transition-colors disabled:cursor-not-allowed"
                            style={{ backgroundColor: color }}
                            title={color}
                          />
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Usage Count */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Usage Count</label>
                    <input
                      type="number"
                      value={formData.usage_count}
                      onChange={(e) => setFormData({...formData, usage_count: parseInt(e.target.value) || 0})}
                      disabled={modalMode === 'view'}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                      placeholder="0"
                      min="0"
                    />
                  </div>

                  {/* Preview */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Preview</label>
                    <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3 p-3 bg-gray-50 rounded-lg">
                      <div
                        className="w-4 h-4 rounded-full border-2 border-white shadow-sm flex-shrink-0"
                        style={{ backgroundColor: formData.color }}
                      />
                      <span className="text-base sm:text-lg font-semibold text-gray-900">{formData.name || 'Tag Name'}</span>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getCategoryColor(formData.category)}`}>
                        {formData.category || 'General'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 px-4 sm:px-6 py-4 sm:flex sm:flex-row-reverse border-t">
                {modalMode !== 'view' ? (
                  <>
                    <button
                      type="button"
                      onClick={handleSave}
                      className="w-full inline-flex justify-center items-center rounded-lg border border-transparent shadow-sm px-4 sm:px-6 py-2 sm:py-3 bg-blue-600 text-sm sm:text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto transition-colors"
                    >
                      {modalMode === 'add' ? 'Create Tag' : 'Update Tag'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setModalOpen(false)}
                      className="mt-3 w-full inline-flex justify-center rounded-lg border border-gray-300 shadow-sm px-4 sm:px-6 py-2 sm:py-3 bg-white text-sm sm:text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 sm:mt-0 sm:ml-3 sm:w-auto transition-colors"
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => setModalOpen(false)}
                    className="w-full inline-flex justify-center rounded-lg border border-gray-300 shadow-sm px-4 sm:px-6 py-2 sm:py-3 bg-white text-sm sm:text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 sm:w-auto transition-colors"
                  >
                    Close
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        isOpen={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Tag"
        message="Are you sure you want to delete this tag? This action cannot be undone and may affect content that uses this tag."
        loading={deleteLoading}
      />
    </div>
  );
};

export default TagsPage;
